// Programs: experiments in order, each presented a set number of times, for each eye; what a
// patient is sent home with, or a clinic runs. Sessions keep a copy of the program they ran, so
// editing a program never changes old results.

import { useState } from "react";
import { api, type Experiment, type Program } from "../api";
import { useAuth } from "../auth";
import { AllCheck, BulkBar, RowCheck, rowKey, useSelection } from "../components/Bulk";
import { Help } from "../components/Help";
import { errorText, useLoad } from "../hooks";
import type { EyeOrder, ProgramStep, Protocol } from "../lib/protocol";
import { EYE_ORDER_LABEL, EYE_ORDERS, experimentSummary, programSummary } from "../lib/summaries";

export function ProgramsPage() {
  const { user } = useAuth();
  const list = useLoad(() => api.programs(), []);
  const experiments = useLoad(() => api.experiments(), []);
  const [editing, setEditing] = useState<Program | "new" | null>(null);
  const [copyFrom, setCopyFrom] = useState<Program | null>(null);
  const [error, setError] = useState<string | null>(null);
  const canEdit = user?.role === "admin" || user?.role === "researcher";
  const def = list.data?.find((p) => p.is_default) ?? list.data?.[0] ?? null;
  const sel = useSelection();
  const isAdmin = user?.role === "admin";
  const shown = (list.data ?? []).map((p) => rowKey("programs", p.id));
  const latest = new Map((experiments.data ?? []).map((e) => [e.id, e]));

  const makeDefault = async (p: Program) => {
    try { await api.makeDefaultProgram(p.id); list.reload(); } catch (e) { setError(errorText(e)); }
  };

  return (
    <>
      <h1>Programs</h1>
      <p className="muted">A program runs experiments in order, each as many times as you choose, for each eye. Give
        programs to a patient on their page; clinic sessions start from one too. The default is used for anyone without one.
        Experiments themselves are made on the <a href="/experiments">Experiments</a> page.</p>
      {error && <div className="error">{error}</div>}
      <div className="card">
        {isAdmin && (
          <BulkBar sel={sel} shown={shown} what="programs" onDeleted={list.reload} presets={[
            { label: "Turned off", keys: (list.data ?? []).filter((p) => !p.active).map((p) => rowKey("programs", p.id)) },
          ]} />
        )}
        <table>
          <thead><tr>{isAdmin && <th className="pick-col"><AllCheck sel={sel} keys={shown} label="Select all programs" /></th>}
            <th>Program</th><th>What it runs</th><th>Home checks</th><th>Version</th><th /></tr></thead>
          <tbody>
            {(list.data ?? []).map((p) => {
              const old = p.protocol.steps.filter((s) => s.experiment_id !== null
                && (latest.get(s.experiment_id)?.version ?? 0) > (s.experiment_version ?? 0));
              return (
                <tr key={p.id} className={`${p.active ? "" : "muted"} ${sel.has(rowKey("programs", p.id)) ? "picked" : ""}`}>
                  {isAdmin && <td className="pick-col"><RowCheck sel={sel} k={rowKey("programs", p.id)} order={shown}
                    label={`Select program ${p.name}`} /></td>}
                  <td><strong>{p.name}</strong>{p.is_default && <> <span className="badge model">default</span></>}
                    {!p.active && <> <span className="badge">off</span></>}
                    <div className="small muted">{p.description}</div></td>
                  <td className="small">{programSummary(p.protocol)}
                    {old.length > 0 && <div className="warn">Runs an older version of {old.map((s) => s.name).join(", ")}.</div>}</td>
                  <td className="small">brightness {p.protocol.home.brightness === "off" ? "off" : "every session"};
                    monitor ID {p.protocol.home.monitor_id}{p.protocol.home.allow_hdr_pq ? "; HDR allowed" : ""}</td>
                  <td>{p.version}</td>
                  <td>
                    {canEdit && <button className="link" onClick={() => { setCopyFrom(null); setEditing(p); }}>Edit</button>}
                    {canEdit && <> · <button className="link" onClick={() => { setCopyFrom(p); setEditing("new"); }}>Copy</button></>}
                    {user?.role === "admin" && !p.is_default && p.active && <> · <button className="link" onClick={() => void makeDefault(p)}>Make default</button></>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {canEdit && def && editing === null && (
          <button style={{ marginTop: "0.8rem" }} onClick={() => { setCopyFrom(def); setEditing("new"); }}>New program</button>
        )}
      </div>
      {editing !== null && experiments.data && (
        <ProgramEditor
          key={editing === "new" ? `new-${copyFrom?.id}` : editing.id}
          program={editing === "new" ? null : editing}
          start={editing === "new" ? (copyFrom ?? def)!.protocol : editing.protocol}
          startName={editing === "new" ? (copyFrom ? `${copyFrom.name} (copy)` : "") : editing.name}
          experiments={experiments.data}
          onDone={() => { setEditing(null); list.reload(); }}
        />
      )}
    </>
  );
}

function num(v: string, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function stepFor(e: Experiment, times = 1): ProgramStep {
  return { experiment_id: e.id, experiment_version: e.version, name: e.name, times, experiment: e.config };
}

function ProgramEditor({ program, start, startName, experiments, onDone }: {
  program: Program | null; start: Protocol; startName: string; experiments: Experiment[]; onDone: () => void;
}) {
  const [name, setName] = useState(startName);
  const [description, setDescription] = useState(program?.description ?? "");
  const [active, setActive] = useState(program?.active ?? true);
  const [p, setP] = useState<Protocol>(structuredClone(start));
  const [json, setJson] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState<number | "">("");
  const Hm = p.home, Al = p.alerts;
  const set = (patch: Partial<Protocol>) => setP({ ...p, ...patch });
  const byId = new Map(experiments.map((e) => [e.id, e]));
  const usable = experiments.filter((e) => e.active);
  const setStep = (i: number, s: ProgramStep) => set({ steps: p.steps.map((x, k) => (k === i ? s : x)) });
  const move = (i: number, d: -1 | 1) => {
    const j = i + d;
    if (j < 0 || j >= p.steps.length) return;
    const steps = [...p.steps];
    [steps[i], steps[j]] = [steps[j]!, steps[i]!];
    set({ steps });
  };
  const tests = p.steps.reduce((n, s) => n + s.times, 0) * p.eyes.length;

  const save = async () => {
    setError(null);
    try {
      const body = { name: name.trim(), description, protocol: p, active };
      if (program) await api.updateProgram(program.id, body);
      else await api.createProgram(body);
      onDone();
    } catch (e) {
      setError(errorText(e));
    }
  };

  return (
    <div className="card">
      <h2 style={{ marginTop: 0 }}>{program ? `Edit ${program.name}` : "New program"}</h2>
      <div className="row">
        <label>Name<input value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label style={{ flex: 2 }}>Description<input value={description} onChange={(e) => setDescription(e.target.value)} /></label>
        {program && !program.is_default && (
          <label style={{ flexDirection: "row", gap: "0.3rem" }}><input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />in use</label>
        )}
      </div>

      <h3>Experiments, in order<Help k="program.steps" ctx={{ program: p }} /></h3>
      <table className="req-table steps">
        <thead><tr><th>#</th><th>Experiment</th><th>Times</th><th>What it does</th><th /></tr></thead>
        <tbody>
          {p.steps.map((s, i) => {
            const e = s.experiment_id !== null ? byId.get(s.experiment_id) : undefined;
            const newer = e && e.version > (s.experiment_version ?? 0);
            return (
              <tr key={i}>
                <td>{i + 1}</td>
                <td>
                  <select aria-label={`Step ${i + 1} experiment`} value={s.experiment_id ?? ""}
                    onChange={(ev) => { const x = byId.get(Number(ev.target.value)); if (x) setStep(i, stepFor(x, s.times)); }}>
                    {s.experiment_id === null && <option value="">{s.name || "(settings only)"}</option>}
                    {experiments.map((x) => <option key={x.id} value={x.id} disabled={!x.active && x.id !== s.experiment_id}>{x.name}</option>)}
                  </select>
                  {newer && <div className="small warn">Saving uses version {e!.version} (this program has {s.experiment_version}).</div>}
                </td>
                <td><input type="number" min={1} max={10} style={{ width: "4.5rem" }} aria-label={`Step ${i + 1} times`} value={s.times}
                  onChange={(ev) => setStep(i, { ...s, times: Math.min(10, Math.max(1, Math.round(num(ev.target.value, s.times)))) })} /></td>
                <td className="small">{experimentSummary(e?.config ?? s.experiment)}</td>
                <td className="row-actions" style={{ whiteSpace: "nowrap" }}>
                  <button className="link" aria-label={`Move step ${i + 1} up`} disabled={i === 0} onClick={() => move(i, -1)}>↑</button>
                  <button className="link" aria-label={`Move step ${i + 1} down`} disabled={i === p.steps.length - 1} onClick={() => move(i, 1)}>↓</button>
                  <button className="link" aria-label={`Remove step ${i + 1}`} disabled={p.steps.length === 1}
                    onClick={() => set({ steps: p.steps.filter((_, k) => k !== i) })}>Remove</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="row" style={{ alignItems: "flex-end", marginTop: "0.5rem" }}>
        <label>Add an experiment
          <select aria-label="Add an experiment" value={adding} onChange={(e) => setAdding(e.target.value ? Number(e.target.value) : "")}>
            <option value="">choose…</option>
            {usable.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
          </select>
        </label>
        <button className="secondary" disabled={adding === ""} onClick={() => {
          const x = adding === "" ? undefined : byId.get(adding);
          if (x) { set({ steps: [...p.steps, stepFor(x)] }); setAdding(""); }
        }}>Add</button>
      </div>

      <div className="row">
        <label><span>Eyes<Help k="program.eyes" ctx={{ program: p }} /></span>
          <select aria-label="Eyes" value={p.eyes.join()} onChange={(e) => set({ eyes: EYE_ORDERS.find(([, v]) => v.join() === e.target.value)![1] })}>
            {EYE_ORDERS.map(([label, v]) => <option key={v.join()} value={v.join()}>{label}</option>)}
          </select>
        </label>
        <label><span>Order<Help k="program.eye_order" ctx={{ program: p }} /></span>
          <select aria-label="Order" value={p.eye_order} onChange={(e) => set({ eye_order: e.target.value as EyeOrder })}>
            {(Object.keys(EYE_ORDER_LABEL) as EyeOrder[]).map((k) => <option key={k} value={k}>{EYE_ORDER_LABEL[k]}</option>)}
          </select>
        </label>
        <label><span>Viewing distance (m)<Help k="program.viewing_distance_mm" ctx={{ program: p }} /></span>
          <input type="number" step="0.05" min="0.3" max="8" value={p.viewing_distance_mm / 1000}
            onChange={(e) => set({ viewing_distance_mm: Math.round(num(e.target.value, 1) * 1000) })} />
        </label>
        <label style={{ flexDirection: "row", gap: "0.3rem", maxWidth: "22rem" }}>
          <input type="checkbox" checked={p.show_each_result} onChange={(e) => set({ show_each_result: e.target.checked })} />
          Show the patient each result right after its test (staff always see it)
        </label>
      </div>
      <p className="small muted">{tests} test{tests === 1 ? "" : "s"} per session.</p>

      <h3>Home checks</h3>
      <p className="small muted" style={{ marginTop: 0 }}>For home patients without requirements of their own: each patient's
        page can set every setup step and session check to mandatory, recommended or not required.</p>
      <div className="row">
        <label>Brightness (meter reading)
          <select aria-label="Brightness check" value={Hm.brightness} onChange={(e) => set({ home: { ...Hm, brightness: e.target.value as typeof Hm.brightness } })}>
            <option value="every_session">before every session</option>
            <option value="off">off</option>
          </select>
        </label>
        <label>Monitor identity (helper app)
          <select aria-label="Monitor identity" value={Hm.monitor_id} onChange={(e) => set({ home: { ...Hm, monitor_id: e.target.value as typeof Hm.monitor_id } })}>
            <option value="off">off</option>
            <option value="optional">optional</option>
            <option value="required">required</option>
          </select>
        </label>
        <label style={{ flexDirection: "row", gap: "0.3rem", maxWidth: "26rem" }}>
          <input type="checkbox" checked={Hm.allow_hdr_pq} onChange={(e) => set({ home: { ...Hm, allow_hdr_pq: e.target.checked } })} />
          Accept HDR (PQ) screens. Contrast then stops at about log CS 2.0; leave off to require 10-bit SDR.
        </label>
      </div>

      <h3>When a result changes</h3>
      <div className="row">
        <label>Baseline
          <select aria-label="Baseline" value={Al.baseline} onChange={(e) => set({ alerts: { ...Al, baseline: e.target.value as typeof Al.baseline } })}>
            <option value="clinic_if_available">first clinic results, else first results</option>
            <option value="clinic_only">first clinic results only</option>
            <option value="any">first results, home or clinic</option>
          </select>
        </label>
        <label>Results in baseline
          <input type="number" min="1" max="5" value={Al.baseline_n} onChange={(e) => set({ alerts: { ...Al, baseline_n: num(e.target.value, 2) } })} />
        </label>
        <label style={{ flexDirection: "row", gap: "0.3rem" }}>
          <input type="checkbox" checked={Al.ask_repeat} onChange={(e) => set({ alerts: { ...Al, ask_repeat: e.target.checked } })} />
          repeat a changed test once, straight away
        </label>
        <label style={{ flexDirection: "row", gap: "0.3rem" }}>
          <input type="checkbox" checked={Al.tell_patient} onChange={(e) => set({ alerts: { ...Al, tell_patient: e.target.checked } })} />
          tell the patient
        </label>
      </div>
      <div className="row">
        <label style={{ flex: 2 }}>Message to the patient
          <textarea rows={2} value={Al.patient_message} onChange={(e) => set({ alerts: { ...Al, patient_message: e.target.value } })} />
        </label>
        <label style={{ flex: 1 }}>How to reach the team
          <input value={Al.contact} placeholder="e.g. call 020 … or email …" onChange={(e) => set({ alerts: { ...Al, contact: e.target.value } })} />
        </label>
      </div>
      <p className="small muted">A result counts as changed when it differs from the baseline by more than the
        repeatability limit (0.15 logMAR, 0.18 log CS). One changed result asks for a repeat; two in a row, the message above.</p>

      <details>
        <summary className="small">Every setting (JSON)</summary>
        <textarea rows={14} style={{ width: "100%", fontFamily: "monospace", fontSize: "0.8rem" }}
          value={json ?? JSON.stringify(p, null, 2)} onChange={(e) => setJson(e.target.value)} />
        {json !== null && (
          <button className="secondary" onClick={() => {
            try { setP(JSON.parse(json) as Protocol); setJson(null); setError(null); } catch (e) { setError(`JSON: ${errorText(e)}`); }
          }}>Use this JSON</button>
        )}
      </details>

      {error && <div className="error">{error}</div>}
      <div className="row" style={{ marginTop: "0.8rem" }}>
        <button disabled={!name.trim() || json !== null || p.steps.length === 0} onClick={() => void save()}>{program ? "Save (new version)" : "Create program"}</button>
        <button className="secondary" onClick={onDone}>Cancel</button>
      </div>
    </div>
  );
}
